import { AbstractDialog } from "../abstract_dialog.js";
import { DialogManager } from "../dialog_manager.js";
import { UIImage } from "../../common_ui_elements/ui_image.js";
import { DialogId } from "../dialog_id.js";
import { ImgID } from "../../../image_loader/img_id.js";
import { LinkedSocialProvider } from "../account_providers_preview_state.js";
import { UINineSliceElement } from "../../common_ui_elements/ui_nine_slice_element.js";
import { MyTextAlign, MyTextVerticalAlign, UITextElement } from "../../common_ui_elements/ui_text_element.js";
import { ColorScheme } from "../../common_ui_elements/color_scheme.js";
import { TextStyleScheme } from "../../common_ui_elements/text_style_scheme.js";
import { RoundedRectangle } from "../../common_ui_elements/rounded_rectangle.js";
import { Vector2 } from "../../../common/vector2.js";
import { AuthProvider } from "../../../api/dto/auth.dto.js";
import type { PlayerDto } from "../../../api/dto/player.dto.js";
import type { LinkResolve } from "../../../api/account_link_service.js";
import { AvatarUtils } from "../../../common/avatar_utils.js";
import { LocalizationManager } from "../../../localization/localization_manager.js";
import { audioController } from "../../../audio/audio_controller.js";
import { AudioClips } from "../../../audio/audio_constants.js";

export interface TwoAccountsDialogOptions
{
  current_player:    PlayerDto;
  current_provider:  AuthProvider;
  existing_player:   PlayerDto;
  existing_provider: AuthProvider;
  on_choose:         ( choice: LinkResolve ) => void | Promise<void>;
}

interface AccountPanelData
{
  provider: AuthProvider;
  player:   PlayerDto;
  choice:   LinkResolve;
}

const DAY_MS = 86_400_000;

export class TwoAccountsDialog extends AbstractDialog
{
  public static readonly dialog_id = DialogId.TWO_ACCOUNTS_DIALOG;

  private static readonly PANEL_W_DESIGN = 493;
  private static readonly PANEL_H_DESIGN = 959;
  private static readonly GAP_DESIGN     = 29;

  private static readonly PLATE_W_DESIGN       = 401;
  private static readonly PLATE_H_DESIGN       = 127;
  private static readonly PLATE_GAP_DESIGN     = 14;
  private static readonly PLATE_START_Y_DESIGN = 207;
  private static readonly PLATE_COUNT          = 4;
  private static readonly PLATE_LABEL_H_DESIGN = 45;
  private static readonly PLATE_CONTENT_Y_OFFSET_DESIGN = 10;

  private static readonly CHOOSE_BTN_W_DESIGN = 358;
  private static readonly CHOOSE_BTN_H_DESIGN = 112;
  private static readonly CHOOSE_BTN_Y_DESIGN = 786;

  private static readonly TOP_PART_W_DESIGN    = 237;
  private static readonly TOP_PART_H_DESIGN    = 128;
  private static readonly TOP_PART_Y_DESIGN    = -80;
  private static readonly TOP_ICON_SIZE_DESIGN = 134;
  private static readonly TOP_ICON_Y_DESIGN    = 55;

  private static readonly TITLE_H_DESIGN = 98;
  private static readonly TITLE_Y_DESIGN = 109;

  private static readonly TITLE_Y_DESIGN_ABOVE = 150;
  private static readonly ATTENTION_Y_DESIGN_BELOW = 40;
  private static readonly ATTENTION_H_DESIGN = 200;

  private close_btn!: UIImage;
  private is_choosing = false;

  constructor( private readonly options: TwoAccountsDialogOptions )
  {
    const design_body_w = TwoAccountsDialog.PANEL_W_DESIGN * 2 + TwoAccountsDialog.GAP_DESIGN;
    const design_body_h = TwoAccountsDialog.PANEL_H_DESIGN;

    const body_w = LayoutScaler.widthFromDesignAdaptive( design_body_w );
    const body_h = LayoutScaler.heightFromDesign( design_body_h );

    const body_x = ( DialogManager.getWidth() - body_w ) / 2;
    const body_y = ( DialogManager.getHeight() - body_h ) / 2;

    super( DialogManager.ctx, body_x, body_y, body_w, body_h );

    const panel_w = LayoutScaler.widthFromDesign( TwoAccountsDialog.PANEL_W_DESIGN );
    const gap     = LayoutScaler.widthFromDesign( TwoAccountsDialog.GAP_DESIGN );

    this.createPanel( 0, panel_w, {
      provider: options.current_provider,
      player:   options.current_player,
      choice:   'keep_current',
    } );
    this.createPanel( panel_w + gap, panel_w, {
      provider: options.existing_provider,
      player:   options.existing_player,
      choice:   'keep_existing',
    } );
    this.createDialogTitle();
    this.createAttentionText();
    this.createCloseButton();
  }

  private getProviderIcon( data: AccountPanelData ): ImgID
  {
    switch ( data.provider )
    {
      case AuthProvider.Google:   return ImgID.GoogleIcon;
      case AuthProvider.Facebook: return ImgID.FacebookIcon;
      case AuthProvider.Apple:    return ImgID.AppleIcon;
      default:                    return AvatarUtils.getAvatarImgId( data.player.avatar_id ?? 0 );
    }
  }

  private getProviderDisplayName( provider: AuthProvider ): string
  {
    switch ( provider )
    {
      case AuthProvider.Google:   return "Google";
      case AuthProvider.Facebook: return "Facebook";
      case AuthProvider.Apple:    return "Apple";
      default:                    return LocalizationManager.t( "TWO_ACCOUNTS_DIALOG.GUEST" );
    }
  }

  private static daysInGame( player: PlayerDto ): string
  {
    const created = Date.parse( player.created_at ?? '' );
    if ( isNaN( created ) ) return "-";
    return String( Math.max( 0, Math.floor( ( Date.now() - created ) / DAY_MS ) ) );
  }

  private static formatStars( points: number | undefined ): string
  {
    return Math.max( 0, Math.floor( points ?? 0 ) ).toLocaleString( "en-US" ).replace( /,/g, " " );
  }

  private static panelStats( player: PlayerDto ): { localization_key: string; value: string }[]
  {
    return [
      { localization_key: "TWO_ACCOUNTS_DIALOG.STAT_ID",    value: String( player.id ) },
      { localization_key: "TWO_ACCOUNTS_DIALOG.STAT_LEVEL", value: String( Math.max( 1, player.player_level ?? 1 ) ) },
      { localization_key: "TWO_ACCOUNTS_DIALOG.STAT_DAYS",  value: TwoAccountsDialog.daysInGame( player ) },
      { localization_key: "TWO_ACCOUNTS_DIALOG.STAT_STARS", value: TwoAccountsDialog.formatStars( player.points ) },
    ];
  }

  private createPanel( panel_x: number, panel_w: number, data: AccountPanelData ): void
  {
    const panel_h = this.rect.getH();

    const bg = new UINineSliceElement(
      this.ctx,
      panel_x,
      LayoutScaler.yFromDesign( 0 ),
      panel_w,
      panel_h,
      ImgID.UI_CapsuleBlue,
      "capsule_bg"
    );
    this.addChild( bg );

    const part_w = LayoutScaler.widthFromDesign( TwoAccountsDialog.TOP_PART_W_DESIGN );
    const part_h = LayoutScaler.heightFromDesign( TwoAccountsDialog.TOP_PART_H_DESIGN );
    const part_x = panel_x + panel_w / 2 - part_w / 2;
    const part_y = LayoutScaler.yFromDesign( TwoAccountsDialog.TOP_PART_Y_DESIGN );

    const bg_part = new UIImage(
      this.ctx,
      part_x,
      part_y,
      part_w,
      part_h,
      ImgID.UI_CapsuleTopBgPart,
      {},
      false
    );
    this.addChild( bg_part );

    const icon_w = LayoutScaler.widthFromDesign( TwoAccountsDialog.TOP_ICON_SIZE_DESIGN );
    const icon_h = LayoutScaler.heightFromDesign( TwoAccountsDialog.TOP_ICON_SIZE_DESIGN );
    const icon_x = Math.ceil( ( part_w - icon_w ) / 2 );
    const icon_y = LayoutScaler.yFromDesign( TwoAccountsDialog.TOP_ICON_Y_DESIGN );

    const provider_icon = new UIImage(
      this.ctx,
      icon_x,
      icon_y,
      icon_w,
      icon_h,
      this.getProviderIcon( data )
    );
    bg_part.addChild( provider_icon, true );

    this.createTitle( panel_x, panel_w, data );
    this.createPlates( panel_x, panel_w, TwoAccountsDialog.panelStats( data.player ) );
    this.createChooseButton( panel_x, panel_w, data.choice );
  }

  private createChooseButton( panel_x: number, panel_w: number, choice: LinkResolve ): void
  {
    const design_font_size = 47;

    const btn_w = LayoutScaler.widthFromDesign( TwoAccountsDialog.CHOOSE_BTN_W_DESIGN );
    const btn_h = LayoutScaler.heightFromDesign( TwoAccountsDialog.CHOOSE_BTN_H_DESIGN );
    const btn_x = panel_x + panel_w / 2 - btn_w / 2;
    const btn_y = LayoutScaler.yFromDesign( TwoAccountsDialog.CHOOSE_BTN_Y_DESIGN );

    const btn = new RoundedRectangle(
      this.ctx, btn_x, btn_y, btn_w, btn_h, btn_h / 2, "#10B2E2", undefined, true
    );

    btn.addChild( new UITextElement(
      this.ctx,
      0,
      0,
      btn_w,
      btn_h,
      {
        localization_key: "CHOOSE_PROGRESS_DIALOG.SELECT",
        text_color: ColorScheme.white,
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        text_style: TextStyleScheme.geologica,
        font_size: LayoutScaler.textSizeFromDesign( design_font_size ),
        font_weight: 700,
        using_y_offset: false,
        ...TextStyleScheme.buttonShadow(),
      }
    ), true );

    btn.setOnClick( () => { this.choose( choice ); } );

    this.addChild( btn );
  }

  private choose( choice: LinkResolve ): void
  {
    if ( this.is_choosing ) return;
    this.is_choosing = true;
    void audioController.playSound( AudioClips.UI_CLICK );
    this.close();
    void this.options.on_choose( choice );
  }

  private createPlates( panel_x: number, panel_w: number, stats: { localization_key: string; value: string }[] ): void
  {
    const plate_w = LayoutScaler.widthFromDesign( TwoAccountsDialog.PLATE_W_DESIGN );
    const plate_h = LayoutScaler.heightFromDesign( TwoAccountsDialog.PLATE_H_DESIGN );
    const gap     = LayoutScaler.heightFromDesign( TwoAccountsDialog.PLATE_GAP_DESIGN );
    const plate_x = panel_x + panel_w / 2 - plate_w / 2;
    const start_y = LayoutScaler.yFromDesign( TwoAccountsDialog.PLATE_START_Y_DESIGN );

    const label_h = LayoutScaler.heightFromDesign( TwoAccountsDialog.PLATE_LABEL_H_DESIGN );
    const content_y_offset = LayoutScaler.heightFromDesign( TwoAccountsDialog.PLATE_CONTENT_Y_OFFSET_DESIGN );

    const design_label_font_size = 40;
    const design_value_font_size = 50;

    for ( let i = 0; i < TwoAccountsDialog.PLATE_COUNT; i++ )
    {
      const plate_y = start_y + i * ( plate_h + gap );

      const plate = new UINineSliceElement(
        this.ctx,
        plate_x,
        plate_y,
        plate_w,
        plate_h,
        ImgID.UI_SettingsLinkBtn,
        "settings_link_btn"
      );
      this.addChild( plate );

      const data = stats[ i ];

      const top_text = new UITextElement(
        this.ctx,
        0,
        content_y_offset,
        plate_w,
        label_h,
        {
          localization_key: data.localization_key,
          text_color: "#B3B9CD",
          text_align: MyTextAlign.Center,
          vertical_align: MyTextVerticalAlign.Center,
          text_style: TextStyleScheme.geologica,
          font_size: LayoutScaler.textSizeFromDesign( design_label_font_size ),
          font_weight: 700,
          using_y_offset: false,
          ...TextStyleScheme.buttonShadow(),
        }
      );
      plate.addChild( top_text, true );

      const bottom_text = new UITextElement(
        this.ctx,
        0,
        label_h + content_y_offset,
        plate_w,
        plate_h - label_h,
        {
          text: data.value,
          text_color: ColorScheme.white,
          text_align: MyTextAlign.Center,
          vertical_align: MyTextVerticalAlign.Center,
          text_style: TextStyleScheme.geologica,
          font_size: LayoutScaler.textSizeFromDesign( design_value_font_size ),
          font_weight: 700,
          using_y_offset: false,
          ...TextStyleScheme.buttonShadow(),
        }
      );
      plate.addChild( bottom_text, true );
    }
  }

  private createTitle( panel_x: number, panel_w: number, data: AccountPanelData ): void
  {
    const design_font_size = 50;
    const design_outline_w = 10;

    const label_h = LayoutScaler.heightFromDesign( TwoAccountsDialog.TITLE_H_DESIGN );
    const label_y = LayoutScaler.yFromDesign( TwoAccountsDialog.TITLE_Y_DESIGN );
    const label_w = panel_w * 0.8;
    const label_x = panel_x + ( panel_w - label_w ) / 2;

    const label = new UITextElement(
      this.ctx,
      label_x,
      label_y,
      label_w,
      label_h,
      {
        text: this.getProviderDisplayName( data.provider ),
        text_color: ColorScheme.white,
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        text_style: TextStyleScheme.geologica,
        font_size: LayoutScaler.textSizeFromDesign( design_font_size ),
        font_weight: 700,
        auto_wrap: true,
        outline_width: LayoutScaler.textBorderSizeFromDesign( design_outline_w ),
        outline_color: ColorScheme.settings_dialog_title_outline,
      }
    );

    this.addChild( label );
  }

  private createDialogTitle(): void
  {
    const design_h = 120;
    const design_font_size = 60;
    const design_outline_w = 20;

    const label_w = this.rect.getW();
    const label_h = LayoutScaler.heightFromDesign( design_h );
    const label_y = -LayoutScaler.yFromDesign( TwoAccountsDialog.TITLE_Y_DESIGN_ABOVE ) - label_h / 2;

    this.addChild( new UITextElement(
      this.ctx,
      0,
      label_y,
      label_w,
      label_h,
      {
        localization_key: "TWO_ACCOUNTS_DIALOG.TITLE",
        text_color: ColorScheme.white,
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        text_style: TextStyleScheme.geologica,
        font_size: LayoutScaler.textSizeFromDesign( design_font_size ),
        font_weight: 700,
        auto_wrap: true,
        outline_width: LayoutScaler.textBorderSizeFromDesign( design_outline_w ),
        outline_color: ColorScheme.booster_dialog_title_outline,
      }
    ) );
  }

  private createAttentionText(): void
  {
    const design_font_size = 36;
    const design_outline_w = 10;

    const label_w = this.rect.getW();
    const label_h = LayoutScaler.heightFromDesign( TwoAccountsDialog.ATTENTION_H_DESIGN );
    const label_y = this.rect.getH() + LayoutScaler.yFromDesign( TwoAccountsDialog.ATTENTION_Y_DESIGN_BELOW );

    this.addChild( new UITextElement(
      this.ctx,
      0,
      label_y,
      label_w,
      label_h,
      {
        localization_key: "TWO_ACCOUNTS_DIALOG.ATTENTION",
        text_color: ColorScheme.white,
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Top,
        text_style: TextStyleScheme.geologica,
        font_size: LayoutScaler.textSizeFromDesign( design_font_size ),
        font_weight: 700,
        auto_wrap: true,
        outline_width: LayoutScaler.textBorderSizeFromDesign( design_outline_w ),
        outline_color: ColorScheme.settings_dialog_title_outline,
      }
    ) );
  }

  private createCloseButton(): void
  {
    const design_btn_size = 117;
    const design_offset_x = 10;
    const design_offset_y = 100;

    const btn_size = LayoutScaler.widthFromDesign( design_btn_size );
    const btn_x = this.rect.getW() - btn_size + LayoutScaler.widthFromDesign( design_offset_x );
    const btn_y = -LayoutScaler.yFromDesign( design_offset_y );

    this.close_btn = new UIImage(
      this.ctx,
      btn_x,
      btn_y,
      btn_size,
      btn_size,
      ImgID.UI_SettingsDialogCloseBtn,
      {},
      true
    );

    this.close_btn.setOnClick( () => this.close() );
    this.addChild( this.close_btn );
  }

  protected override playAppearAnimation(
    from_scale      = 0.7,
    overshoot_scale = 1.1,
    final_scale     = 1.0,
    scale_up_duration = 136,
    settle_duration   = 96
  ): void
  {
    this.close_btn.setScale( new Vector2( 0, 0 ) );

    super.playAppearAnimation( from_scale, overshoot_scale, final_scale, scale_up_duration, settle_duration );

    this.popCloseButtonAfterAppear( this.close_btn, scale_up_duration + settle_duration );
  }

  public name(): string { return "TwoAccountsDialog"; }

  public render(): void
  {
    if ( !this.shouldBeRendered() ) return;
    this.startRender();
    this.finishRender();
  }
}
